import React from 'react';
import { Box, Grid, LinearProgress, Typography } from '@mui/material';
import sparkLoadingAnimation from 'animations/sparkLoading.json';
import { UserContext, UserContextValue } from 'contexts/UserContext';
import { Cookies } from 'react-cookie';
import { useApplicationUserByID } from 'hooks/ApplicationUser';
import Lottie from 'react-lottie';
import { useUserStore } from 'stores';
import { Error, ErrorStatusIcon } from 'components/SVG';

interface UserContextProps {
  children: React.ReactNode;
}

const UserContextProvider = ({ children }: UserContextProps) => {
  const setApplicationUser = useUserStore(
    React.useCallback((state) => state.setApplicationUser, [])
  );
  const cookies = new Cookies();
  const userID = cookies.get('userID') as string;

  const { applicationUser, isLoading, isFetching } =
    useApplicationUserByID(userID);

  React.useEffect(() => {
    if (applicationUser) {
      setApplicationUser(applicationUser);
    }
  }, [applicationUser, setApplicationUser]);

  const isLoadingOrFetching = isLoading || isFetching;

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: sparkLoadingAnimation,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  if (isLoadingOrFetching) {
    return (
      <Grid
        height='100vh'
        container
        justifyContent='center'
        alignItems='center'
      >
        <Box display='flex' alignItems='center' flexDirection='column' gap={1}>
          <Lottie options={defaultOptions} height={100} width={100} />
          <Typography
            color={(theme) => theme.palette.grey[800]}
            variant='h6'
            fontFamily='Mont-semibold'
          >
            Loading User...
          </Typography>
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
        </Box>
      </Grid>
    );
  }
  if (!!applicationUser) {
    setApplicationUser(applicationUser);
  }

  if (!applicationUser && !!userID) {
    return (
      <Grid
        height='100vh'
        container
        justifyContent='center'
        alignItems='center'
      >
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          flexDirection='column'
          gap={2}
        >
          <ErrorStatusIcon width={100} height={100} />
          <Box ml={2} mr={2} textAlign='center' mt={2}>
            <Typography
              variant='h5'
              fontFamily='Mont-semibold'
              marginBottom={2}
            >
              Oops! We couldn’t load your information.
            </Typography>
            <Typography variant='body1' textAlign={'center'}>
              Please try again later.
            </Typography>
          </Box>
        </Box>
      </Grid>
    );
  }

  const contextValue: UserContextValue = {
    currentUser: applicationUser,
  };

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};

export default UserContextProvider;
